import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          type: 'bot',
          text: 'Hallo! Ich bin Ihr POSITIV Assistent. Wie kann ich Ihnen helfen?',
          suggestions: [
            'Öffnungszeiten',
            'Mitgliedschaft & Preise',
            'Kurse & Zeiten',
            'Spa-Bereich',
            'Kontakt'
          ]
        }
      ]);
    }
  }, [isOpen]);

  const faqResponses = {
    öffnungszeiten: {
      text: 'Unsere Öffnungszeiten:\n\nMontag - Freitag: 6:00 - 22:00 Uhr\nSamstag - Sonntag: 8:00 - 20:00 Uhr\n\nWir sind an allen Feiertagen geschlossen.',
      suggestions: ['Mitgliedschaft & Preise', 'Kurse & Zeiten', 'Kontakt']
    },
    'mitgliedschaft & preise': {
      text: 'Wir bieten drei Mitgliedschaftsmodelle:\n\n🥇 POSITIV Platin Label - 24 Monate\nab €11 / Woche\n\n🥇 POSITIV Platin Label - 12 Monate\nab €13 / Woche\n\n🥇 POSITIV Platin Label - 1 Monat\nab €19 / Woche\n\nAlle Mitgliedschaften beinhalten:\n• Training in allen Clubs\n• Zugang zu allen Kursen\n• Wellness & Sauna\n• Getränkeflat',
      suggestions: ['Kurse & Zeiten', 'Spa-Bereich', 'Kontakt']
    },
    'kurse & zeiten': {
      text: 'Unsere beliebten Kurse:\n\n💪 Kraft: Bodypump, Workout\n🏃 Cardio: Cycling, Jumping\n🧘 Wohlbefinden: Rücken-Fit, Mobility, Rücken-Mobility\n⚡ Express: Bauch Express (30 Min)\n🏥 Reha: Rehasport\n\nDen kompletten Wochenplan finden Sie auf unserer Kurse-Seite.\n\nMöchten Sie mehr über einen bestimmten Kurs erfahren?',
      suggestions: ['Bodypump', 'Cycling', 'Rehasport', 'Personal Training']
    },
    'spa-bereich': {
      text: 'Unser Wellness-Bereich steht allen Mitgliedern kostenlos zur Verfügung:\n\n🔥 Finnische Sauna\n💡 Infrarot Kabine\n☀️ Solarium\n🛋️ Ruheräume\n\nPerfekt zur Regeneration nach dem Training!',
      suggestions: ['Mitgliedschaft & Preise', 'Öffnungszeiten', 'Kontakt']
    },
    kontakt: {
      text: '📍 Adresse:\nPOSITIV Fitness Studio\nHauptstraße 123\n85283 Wolnzach\n\n📞 Telefon: +49 (0) 8442 123456\n📧 E-Mail: info@positiv-wolnzach.de\n\nGerne können Sie uns auch über unser Kontaktformular erreichen!',
      suggestions: ['Öffnungszeiten', 'Mitgliedschaft & Preise', 'Kurse & Zeiten']
    },
    bodypump: {
      text: '💪 Bodypump\n\nKrafttraining mit der Langhantel für den ganzen Körper. Definiert und stärkt alle Hauptmuskelgruppen.\n\n⏱️ Dauer: 60 Min\n📊 Intensität: Hoch\n\nTermine finden Sie in unserem Wochenplan auf der Kurse-Seite.',
      suggestions: ['Cycling', 'Workout', 'Kurse & Zeiten']
    },
    cycling: {
      text: '🚴 Cycling\n\nMitreißendes Indoor-Cycling mit energiegeladener Musik. Verbrennt Kalorien und stärkt die Ausdauer.\n\n⏱️ Dauer: 60 Min\n📊 Intensität: Hoch\n\nTermine finden Sie in unserem Wochenplan auf der Kurse-Seite.',
      suggestions: ['Bodypump', 'Jumping', 'Kurse & Zeiten']
    },
    workout: {
      text: '🏋️ Workout\n\nIntensives Ganzkörper-Workout für maximale Kraft und Ausdauer. Funktionelle Übungen für alle Fitnesslevel.\n\n⏱️ Dauer: 50 Min\n📊 Intensität: Hoch',
      suggestions: ['Bodypump', 'Kurse & Zeiten']
    },
    jumping: {
      text: '🦘 Jumping\n\nDynamisches Trampolintraining für Kraft, Koordination und Spaß. Gelenkschonendes Cardio-Workout.\n\n⏱️ Dauer: 50 Min\n📊 Intensität: Hoch',
      suggestions: ['Cycling', 'Kurse & Zeiten']
    },
    rehasport: {
      text: '🏥 Rehasport\n\nTherapeutisches Training zur Rehabilitation und Vorbeugung. Schonend und effektiv für Ihren Körper.\n\n⏱️ Dauer: 45 Min\n📊 Intensität: Niedrig\n\nViele Termine pro Woche verfügbar!',
      suggestions: ['Mobility', 'Kurse & Zeiten']
    },
    mobility: {
      text: '🧘 Mobility & Rücken-Fit\n\nGanzheitliches Beweglichkeitstraining für alle Gelenke. Fördert Flexibilität und Regeneration.\n\n⏱️ Dauer: 50-60 Min\n📊 Intensität: Niedrig-Mittel\n\nPerfekt zur Regeneration!',
      suggestions: ['Rehasport', 'Kurse & Zeiten']
    },
    'personal training': {
      text: '👤 Personal Training\n\nPräzisionsbasiertes 1:1 Training mit zertifizierten Coaches.\n\n✓ Individuelle Trainingspläne\n✓ Regelmäßige Fortschrittskontrollen\n✓ Ernährungsberatung\n✓ Flexible Terminplanung\n\nBuchen Sie Ihre kostenlose Erstberatung!',
      suggestions: ['Kontakt', 'Mitgliedschaft & Preise']
    }
  };

  const findBestMatch = (input) => {
    const lowerInput = input.toLowerCase();
    
    // Exakte Übereinstimmungen
    for (const key in faqResponses) {
      if (lowerInput.includes(key)) {
        return faqResponses[key];
      }
    }
    
    // Keyword-Matching
    if (lowerInput.includes('öffnung') || lowerInput.includes('geöffnet') || lowerInput.includes('wann')) {
      return faqResponses['öffnungszeiten'];
    }
    if (lowerInput.includes('preis') || lowerInput.includes('kosten') || lowerInput.includes('mitglied')) {
      return faqResponses['mitgliedschaft & preise'];
    }
    if (lowerInput.includes('kurs') || lowerInput.includes('training') || lowerInput.includes('wochenplan')) {
      return faqResponses['kurse & zeiten'];
    }
    if (lowerInput.includes('sauna') || lowerInput.includes('wellness') || lowerInput.includes('spa')) {
      return faqResponses['spa-bereich'];
    }
    if (lowerInput.includes('kontakt') || lowerInput.includes('adresse') || lowerInput.includes('telefon') || lowerInput.includes('email')) {
      return faqResponses['kontakt'];
    }
    
    return null;
  };

  const handleSendMessage = (text = inputValue) => {
    if (!text.trim()) return;

    // User-Nachricht hinzufügen
    const userMessage = { type: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Bot-Antwort finden
    setTimeout(() => {
      const response = findBestMatch(text);
      
      if (response) {
        setMessages(prev => [...prev, { type: 'bot', ...response }]);
      } else {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: 'Entschuldigung, dazu habe ich leider keine Informationen. Wählen Sie bitte eine der folgenden Optionen:',
          suggestions: ['Öffnungszeiten', 'Mitgliedschaft & Preise', 'Kurse & Zeiten', 'Spa-Bereich', 'Kontakt']
        }]);
      }
    }, 500);
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-black text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-transform"
          aria-label="Chat öffnen"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] bg-white rounded-lg shadow-2xl flex flex-col border border-black/10">
          {/* Header */}
          <div className="bg-black text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageCircle className="w-5 h-5" />
              <div>
                <h3 className="font-bold">POSITIV Assistent</h3>
                <p className="text-xs text-white/70">Wir helfen Ihnen gerne</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-1 rounded transition-colors"
              aria-label="Chat schließen"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div key={index} className="space-y-2">
                <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.type === 'user'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-black/10'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                  </div>
                </div>
                
                {/* Suggestions */}
                {message.type === 'bot' && message.suggestions && (
                  <div className="flex flex-wrap gap-2 pl-2">
                    {message.suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="text-xs bg-white border border-black/20 text-black px-3 py-1.5 rounded-full hover:bg-black hover:text-white transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-black/10 bg-white rounded-b-lg">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              className="flex gap-2"
            >
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ihre Frage..."
                className="flex-1 border-black/20"
              />
              <Button
                type="submit"
                className="bg-black text-white hover:bg-black/90"
                size="icon"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
